import { useRef } from "react";

import { ReactComponent as VisaIcon } from "assets/icons/visa-icon.svg";
import { ReactComponent as MastercardIcon } from "assets/icons/mastercard-icon.svg";

import InputFieldWithValidation, { IInputFieldWithValidationProps } from "components/InputFieldWithValidation";
import InputFieldLabel from "components/InputFieldComponents/InputFieldLabel";
import { PaymentSourceInputName } from "v2/dataroutes/BillingData";


export type CardType = 'amex' | 'other';

// Amex BINs always start with 34 or 37 (15-digit numbers).
const AMEX_BIN_REGEX = /^3[47]/;

// Matches the on-screen formatted value: Amex 4-6-5 or Visa/MC 4-4-4-4.
// Explicit groupings (vs. a length-only check) prevent partially-typed numbers
// like "4242 4242 4242 42" (14 digits, 17 chars) passing as complete.
const CARD_NUMBER_PATTERN = "(\\d{4} \\d{6} \\d{5}|\\d{4} \\d{4} \\d{4} \\d{4})";


export default function CardNumberInput(
  {
    name,
    validate = true,
    onCardTypeChange,
    ...props
  }: {
    name?: string,
    validate?: boolean,
    onCardTypeChange?: (cardType: CardType) => void,
  } & IInputFieldWithValidationProps
) {

  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const unformatCardNumber = (cardNumberStr: string, cursorPosition: number | null): [ string, number | null ] => {
    const nonNullCursorPosition = cursorPosition || 0;
    const valueBeforeCursor = cardNumberStr.slice(0, nonNullCursorPosition);
    const numbersBeforeCursor = valueBeforeCursor.replace(/\D/g, "");
    const realCursorPosition = nonNullCursorPosition - (valueBeforeCursor.length - numbersBeforeCursor.length);

    const allDigits = numbersBeforeCursor + cardNumberStr.slice(nonNullCursorPosition).replace(/\D/g, "");
    const isAmex = AMEX_BIN_REGEX.test(allDigits);
    const maxDigits = isAmex ? 15 : 16;
    const truncated = allDigits.slice(0, maxDigits);

    onCardTypeChange?.(isAmex ? 'amex' : 'other');

    return [ truncated, Math.min(realCursorPosition, maxDigits) ];
  };

  const formatCardNumber = (cardNumber: string | ReadonlyArray<string> | number | undefined, cursorPosition?: number | null): [ string, number | null ] => {
    if (typeof cardNumber !== "string") {
      return [ "", 0 ];
    }

    const groups = AMEX_BIN_REGEX.test(cardNumber) ? [ 4, 6, 5 ] : [ 4, 4, 4, 4 ];

    let formatted = cardNumber.slice(0, groups[0]);
    let consumed = groups[0];
    let cursorShift = 0;
    for (let i = 1; i < groups.length && consumed < cardNumber.length; i++) {
      formatted += " " + cardNumber.slice(consumed, consumed + groups[i]);
      if (cursorPosition && cursorPosition > consumed) cursorShift++;
      consumed += groups[i];
    }

    return [ formatted, cursorPosition ? cursorPosition + cursorShift : null ];
  };


  return (
    <>
      <input type="hidden" name={ name || PaymentSourceInputName.card.number } ref={ hiddenInputRef } />
      <InputFieldWithValidation
        { ...props }
        label={ <InputFieldLabel label="Card Number" required={ validate } /> }
        type="text"
        placeholder="XXXX XXXX XXXX XXXX"
        value=""
        formatValue={ formatCardNumber }
        unformatValue={ unformatCardNumber }
        unformattedInputRef={ hiddenInputRef }
        required={ validate }
        pattern={ validate ? CARD_NUMBER_PATTERN : undefined }
        patternMismatchErrorMessage="Please enter a valid card number."
        // TODO pass isValidated property, but only after the form is submitted at least once
        TrailingElement={ (
          <div className="flex flex-row gap-x-1">
            <VisaIcon />
            <MastercardIcon />
          </div>
        ) }
        inputPaddingForTrailingElement="pr-20"
      />
    </>
  );
};
